import React from 'react';
import classNames from 'classnames';

// min1,max1, min2,max2, ...
function nearestValid(cur, values, prev) {
  // find FIRST group with max >= cur
  let left = 0, right = values.length >> 1;
  while (right - left > 1) {
    const mid = (left + right - 1) >> 1;
    if (values[mid + mid + 1] < cur) {
      left = mid + 1;
    } else {
      right = mid + 1;
    }
  }
  left *= 2;
  if (cur >= values[left] && cur <= values[left + 1]) {
    return cur;
  } else if (cur > values[left + 1]) {
    return values[left + 1];
  } else if (left > 0 && prev > cur) {
    return values[left - 1];
  } else {
    return values[left];
  }
}

const NumberInput = React.forwardRef(function NumberInput({ digits=2, value=0, onChange, onEnter, className, values, onFocus: onFocusOrig, onBlur: onBlurOrig, onlyFinal, trim=true, ...props }, ref) {
  const { disabled, min, max, step=1 } = props;
  const [valid, setValid] = React.useState(true);
  const [refresh, setRefresh] = React.useState(0);
  const valueRef = React.useRef(value);

  const inputRef = React.useRef();
  React.useImperativeHandle(ref, () => ({
    focus() {
      inputRef.current.focus();
    }
  }), []);

  const fmtValue = React.useCallback(value => (trim ? parseFloat(value.toFixed(digits)) : value.toFixed(digits)), [trim, digits]);

  React.useLayoutEffect(() => {
    if (valueRef.current !== value) {
      valueRef.current = value;
      inputRef.current.value = fmtValue(value);
      setValid(true);
    }
  }, [value, refresh, fmtValue]);

  const onFocus = React.useCallback(e => {
    inputRef.current.select();
    if (onFocusOrig) onFocusOrig(e);
  }, [onFocusOrig]);

  const onInput = React.useCallback(e => {
    const m = e.target.value.match(/^(\d*)(?:\.(\d*))?$/);
    const input = parseFloat(e.target.value);
    if (!m || isNaN(input)) {
      setValid(false);
      valueRef.current = null;
    } else {
      const d = Math.max(digits, m[2] ? m[2].length : 0);
      const eps = Math.exp(-Math.LN10 * d) * 0.51;
      let clamped = input;
      if (step && !isNaN(step)) clamped = Math.round(clamped / step) * step;
      if (min != null) clamped = Math.max(min, clamped);
      if (max != null) clamped = Math.min(max, clamped);
      if (values) clamped = nearestValid(clamped, values, value);
      if (Math.abs(clamped - input) > eps) {
        if (valid && Math.abs(input - value) === step) {
          inputRef.current.value = clamped;
          valueRef.current = clamped;
          setValid(true);
          if (!onlyFinal) {
            onChange(clamped);
          }
        } else {
          valueRef.current = null;
          setValid(false);
        }
      } else {
        valueRef.current = clamped;
        setValid(true);
        if (!onlyFinal) {
          onChange(clamped);
        }
      }
    }
  }, [digits, min, max, step, onChange, value, values, valid, onlyFinal]);

  React.useEffect(() => {
    if (disabled) return;
    function onWheel(e) {
      e.preventDefault();
      e.stopPropagation();
      const dir = (e.deltaY > 0 ? -1 : 1);
      let clamped = value;
      if (step && !isNaN(step)) clamped += step * dir;
      else clamped += dir;
      if (min != null) clamped = Math.max(min, clamped);
      if (max != null) clamped = Math.min(max, clamped);
      if (values) clamped = nearestValid(clamped, values, value);
      inputRef.current.value = fmtValue(clamped);
      valueRef.current = clamped;
      onChange(clamped);
      setValid(true);
    }
    const node = inputRef.current;
    node.addEventListener("wheel", onWheel, {passive: false});
    return () => node.removeEventListener("wheel", onWheel, {passive: false});
  }, [value, onChange, min, max, step, values, fmtValue, disabled]);

  const onAccept = React.useCallback(e => {
    let input = parseFloat(inputRef.current.value);
    if (isNaN(input)) {
      valueRef.current = value;
      inputRef.current.value = fmtValue(value);
      setValid(true);
    } else {
      if (step && !isNaN(step)) input = Math.round(input / step) * step;
      if (min != null) input = Math.max(min, input);
      if (max != null) input = Math.min(max, input);
      if (values) input = nearestValid(input, values, value);
      valueRef.current = input;
      inputRef.current.value = fmtValue(input);
      setValid(true);
      onChange(input);
      setRefresh(v => v + 1);
    }
    if (onBlurOrig) onBlurOrig(e);
  }, [min, max, step, value, values, onChange, onBlurOrig, fmtValue]);

  const onCancel = React.useCallback(() => {
    valueRef.current = value;
    inputRef.current.value = fmtValue(value);
    setValid(true);
  }, [value, fmtValue]);

  const onKeyDown = React.useCallback(e => {
    if (disabled) {
      return;
    }
    switch (e.key) {
    case "Enter":
      inputRef.current.blur();
      if (onEnter) onEnter(valueRef.current);
      break;
    case "Esc":
    case "Escape":
      onCancel();
      inputRef.current.blur();
      break;
    // no default
    }
  }, [disabled, onEnter, onCancel]);

  return <input type="number"
                defaultValue={fmtValue(value)}
                onChange={onInput}
                onKeyDown={onKeyDown}
                onFocus={onFocus}
                onBlur={onAccept}
                className={classNames(className, {"ui-Input-invalid": !valid})}
                ref={inputRef}
                {...props}/>;
});

export default NumberInput;
